import { useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { MenuItemOption } from "../models/nlp/menu/MenuItemOption";
import MenuItemModel from "../models/nlp/menu/MenuItemModel";
import { MenuItemOptionGroup } from "../models/nlp/menu/MenuItemOptionGroup";
import { MenuItemSizeGroup } from "../models/nlp/menu/MenuItemSizeGroup";
import * as voicifyApi from "../api";
import ApplicationNlpMenuContainer from "./ApplicationNlpMenuContainer";
import { MenuItemSize } from "../models/nlp/menu/MenuItemSize";
import UpdateMenuRequest from "../models/nlp/menu/UpdateMenuRequest";
import { SyncMenusRequest } from "../models/nlp/menu/SyncMenusRequest";
import ApplicationModel from "../models/applications/api/ApplicationModel";
import { POSNames } from "../models/nlp/PosNames";
import { v4 as uuidv4 } from 'uuid';
import _ from "lodash";
import IResult from "../models/result/IResult";
import EditedMenuItemModel from "../models/nlp/menu/EditedMenuItemModel";
import InteractionModelContainer from "./InteractionModelContainer";
import MenuSyncContainer from "./MenuSyncContainer";
import { GenAIFoodOrdering } from "../constants/featureFlagIds";
import UpdateAppliedApplicationTemplateFormRequest from "../models/templating/api/UpdateAppliedApplicationTemplateFormRequest";


interface Value {
    original: string
    current: string
}

export interface Diff {
    key: string
    value: Value
}

export interface PendingUpdate {
    menuItemId: string
    menuItemName: string
    diffs: Diff[]
}

interface MatchedItems {
    updatedItem: MenuItemModel
    diffs: DiffDetails[]
}

interface DiffDetails {
    key: string,
    diff: string,
    whatsChanged?: string,
    newValue: string | boolean
    originalValue: string | boolean
}

export const getMenuSyncModuleId = (): string => {

    if (process.env.VOICIFY_ENVIRONMENT == "dev" || process.env.VOICIFY_ENVIRONMENT == "staging") {
        return "af153c7e-faed-4068-9ee7-5dba38a1aca5"; // todo: figure out a module id for dev.
    }
    else if (process.env.VOICIFY_ENVIRONMENT == "production") {
        return "af153c7e-faed-4068-9ee7-5dba38a1aca5";
    }
    return null;

};
function useMenuContainer() {
    const [liveMenuItems, setLiveMenuItems] = useState<MenuItemModel[]>([]);
    const [fetchingMenu, setFetchingMenu] = useState<boolean>(false);
    const [menuItems, setMenuItems] = useState<MenuItemModel[]>([]);
    const [menuItem, setMenuItem] = useState<MenuItemModel>(null);
    const [originalMenuItem, setOriginalMenuItem] = useState<MenuItemModel>(null)
    const [categories, setCategories] = useState<string[]>([]);
    const [activeEditId, setActiveEditId] = useState("");
    const [isMenuItemEdited, setIsMenuItemEdited] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [loadingPOSMenuSync, setLoadingPOSMenuSync] = useState<boolean>(false);
    const [ladingPublishAllChanges, setLoadingPublishAllChanges] = useState<boolean>(false);
    const [matchingItemsWithSameOptionGroupOrSizeGroup, setmatchingItemsWithSameOptionGroupOrSizeGroup] = useState<MatchedItems[]>();
    const [showAskToUpdateOtherItemsModal, setShowAskToUpdateOtherItemsModal] = useState<boolean>(false);
    const [itemsToNotAugment, setItemsToNotAugment] = useState<string[]>([]);
    const [pendingMenuItemUpdates, setPendingMenuItemUpdates] = useState<PendingUpdate[]>([]);
    const [sizeName, setSizeName] = useState("");
    const [optionGroupsToAugment, setOptionGroupsToAugment] = useState<{ menuItemId: string, optionGroupRefId: string }[]>([]);
    const [loadingMenuDiff, setLoadingMenuDiff] = useState<boolean>(false);
    const [menuSyncRequest, setMenuSyncRequest] = useState<UpdateAppliedApplicationTemplateFormRequest>(null);
    const [path, setPath] = useState<{
        text: string,
        pathLength: number,
        selectedOption: MenuItemOption,
        selectedOptionGroup: MenuItemOptionGroup,
        selectedSizeGroup: MenuItemSizeGroup,
        selectedSize: MenuItemSize
    }[]>([] as any);

    const menuSyncContainer = MenuSyncContainer.useContainer();

    const [isLoadingMenu, setIsLoadingMenu] = useState(false);
    const [menuItemAugmentStatus, setMenuItemAugmentStatus] = useState<"success" | "loading" | "fail" | null>(null);
    const applicationNlpMenuContainer = ApplicationNlpMenuContainer.useContainer();
    const interactionModelContainer = InteractionModelContainer.useContainer();

    async function retrieveEntireMenu(applicationId: string): Promise<IResult<MenuItemModel[]>> {
        var menuItemResult = await voicifyApi.getVoicifyMenuItems(applicationId);
        const checkOptionGroup = (group: MenuItemOptionGroup) => {
            if (!group.referenceId?.length) {
                group.referenceId = uuidv4();
            }
            for (const option of group.options) {
                if (option?.optionGroups?.length) {
                    for (const subGroup of option.optionGroups) {
                        checkOptionGroup(subGroup);
                    }
                }
            }
        }
        const checkSizeGroup = (group: MenuItemSizeGroup) => {
            if (group?.sizes?.length) {
                for (const size of group.sizes) {
                    if (size?.optionGroups?.length) {
                        for (const subGroup of size.optionGroups) {
                            checkOptionGroup(subGroup);
                        }
                    }
                }
            }
        }
        // ensure all option groups have reference ids
        if (menuItemResult?.data?.length) {
            for (const item of menuItemResult.data) {
                if (item.optionGroups?.length) {
                    for (const group of item.optionGroups) {
                        checkOptionGroup(group);
                    }
                }
                if (item.sizeGroups?.length) {
                    for (const group of item.sizeGroups) {
                        checkSizeGroup(group);
                    }
                }
            }
        }
        return menuItemResult;
    }

    useEffect(() => {
        if (!menuItems?.length) {
            setPendingMenuItemUpdates([]);
            return;
        }
        if (!liveMenuItems?.length) {
            setPendingMenuItemUpdates([]);
            return;
        }
        let updates = [];
        for (const item of menuItems) {
            const liveItem = liveMenuItems.find(i => i.id === item.id);
            if (item.isDisabled) {
                if (liveItem) {
                    updates.push({ menuItemId: item.id, menuItemName: item.originalName, diffs: [] });
                }
                continue;
            }
            if (!liveItem) {
                updates.push({ menuItemId: item.id, menuItemName: item.originalName, diffs: [] });
                continue;
            }
            // identify properties that are different between live item and item
            const copy1 = JSON.parse(JSON.stringify(item));
            const copy2 = JSON.parse(JSON.stringify(liveItem));
            copy1.applicationId = "";
            copy2.applicationId = "";
            const diffs = findChangesInMenuItems(copy1, copy2, true);
            if (diffs.length) {
                updates.push({ menuItemId: item.id, menuItemName: item.originalName, diffs });
            }
        }
        setPendingMenuItemUpdates(updates);

    }, [menuItems, liveMenuItems])

    async function getLiveMenu(applicationid: string) {
        try {
            const menuResponse = await voicifyApi.getVoicifyMenuItems(applicationid);
            if (menuResponse.resultType === "Ok" && menuResponse.data.length > 0) {
                setLiveMenuItems([...menuResponse.data])
            }
        } catch {
        }
    }
    async function getMenu(applicationId: string) {
        try {
            setIsLoadingMenu(true);
            const menuResponse = await retrieveEntireMenu(applicationId)
            if (menuResponse.errors && menuResponse.errors.length > 0) {
                setErrors(menuResponse.errors)
            }
            if (menuResponse.resultType === "Ok" && menuResponse.data.length > 0) {
                setMenuItems([...menuResponse.data])

                const categoriesSet: Set<string> = new Set<string>()
                categoriesSet.add("All")
                for (const item of menuResponse.data) {
                    if (typeof item.attributes?.data?.category !== "undefined") {
                        categoriesSet.add(item.attributes?.data.category)
                    }
                }
                if (categoriesSet.size === 1) { categoriesSet.delete("All") }
                setCategories(Array.from(categoriesSet.values()))
            }
            setIsLoadingMenu(false);
        } catch (error) {
            setIsLoadingMenu(false);
            setErrors([error])
        }
    }

    async function loadMenuSyncModule(applicationId: string) {
        const appliedTemplates = await voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
        if (appliedTemplates?.resultType == "Ok") {
            const appliedModule = appliedTemplates?.data?.find(t => t?.templateForm?.templateConfigurationId === getMenuSyncModuleId())
            if (appliedModule) {
                // found a menu sync module applied here. Set the request.
                const request: UpdateAppliedApplicationTemplateFormRequest = {
                    templateFormFieldValues: [
                        ...appliedModule?.templateFormFieldValues,
                    ],
                    name: appliedModule.name ?? "",
                    appliedTemplateId: appliedModule?.id
                };
                setMenuSyncRequest(request);
                return;
            }
        }
        setMenuSyncRequest(null);
    }

    async function submitMenuSyncRequest(applicationId: string) {
        if (menuSyncRequest) {
            await voicifyApi.finalizeDynamicForm(menuSyncRequest.appliedTemplateId, {
                applicationId: applicationId,
                data: menuSyncRequest
            });
        }
    }

    async function getMenuItem(applicationId: string, menuItemId: string) {
        try {
            setIsLoadingMenu(true);
            const menuResponse = await voicifyApi.getVoicifyMenuItems(applicationId, menuItemId);
            if (menuResponse.errors && menuResponse.errors.length > 0) {
                setErrors(menuResponse.errors);
                setIsLoadingMenu(false);
            }
            if (menuResponse.resultType === "Ok" && menuResponse.data.length > 0) {
                setMenuItem({ ...menuResponse.data[0] })
                setOriginalMenuItem(JSON.parse(JSON.stringify(menuResponse.data[0])))
                setIsLoadingMenu(false);
                return menuResponse.data[0]
            }
            setIsLoadingMenu(false);
        } catch (error) {
            setIsLoadingMenu(false);
            setErrors([error])
        }
    }

    function resetMenuItemSelections() {
        setMenuItem(null);
        setActiveEditId(null);
        setIsMenuItemEdited(false);
        setPath([]);
    }

    function updateMenuItem(menuItem: MenuItemModel, changedObj: MenuItemModel | MenuItemModel["attributes"] | MenuItemOptionGroup, changedProperty: string,
        persistentPropertiesOverride?: { changedObj: MenuItemModel | MenuItemModel["attributes"] | MenuItemOptionGroup, changedProperty: string }) {
        //do not allow updates during the augment process
        if (menuItemAugmentStatus) {
            return;
        }
        // the data dictionary can't have persistent properties. add the data dictionary to persistent properties instead via persistent properties overide
        if (persistentPropertiesOverride) {
            if (!persistentPropertiesOverride.changedObj.persistentProperties?.length) {
                persistentPropertiesOverride.changedObj.persistentProperties = [];
            }
            if (!persistentPropertiesOverride.changedObj.persistentProperties.includes(persistentPropertiesOverride.changedProperty) && persistentPropertiesOverride.changedProperty?.length) {
                persistentPropertiesOverride.changedObj.persistentProperties.push(persistentPropertiesOverride.changedProperty)
            }
        }
        else {
            if (!changedObj.persistentProperties?.length) {
                changedObj.persistentProperties = [];
            }
            if (!changedObj.persistentProperties.includes(changedProperty) && changedProperty?.length) {
                changedObj.persistentProperties.push(changedProperty)
            }
        }

        setIsMenuItemEdited(true);
        setMenuItem({ ...menuItem })
    }

    async function updateMenu(updatedItem: MenuItemModel, applicationId: string) {
        await applicationNlpMenuContainer.updateVoicifyMenuItem(applicationId, updatedItem);
    }

    function handleDisableEnableCategory(categoryIndexToUpdate: number, enableDisable: boolean, applicationId: string) {

        const filteredItems = menuItems.filter(
            ({ attributes }) => attributes?.data?.category === categories[categoryIndexToUpdate]
        );

        const updatedMenuItems = menuItems.map(item => {
            const matchingItem = filteredItems.find(filteredItem => filteredItem.id === item.id);

            //Since "All" is the first Category, it needs to go through all the items
            if (categoryIndexToUpdate === 0) {
                if ((item.isDisabled ?? false) !== enableDisable) { //Only call the API if isDisabled is changed
                    const result = { ...item, isDisabled: enableDisable };
                    updateMenu(result, applicationId);
                    return result;
                }
                return item;
            } else {
                if (matchingItem && (item.isDisabled ?? false) !== enableDisable) { //Only call the API if isDisabled is changed
                    const result = { ...item, isDisabled: enableDisable };
                    updateMenu(result, applicationId);
                    return result;
                }
                return item;
            }


        });

        setMenuItems(updatedMenuItems);
        setShowConfirmationModal(false);

    }

    async function syncMenuWithPOS(application: ApplicationModel, options: { retainAugmentation: boolean, retainMenuBooleans: boolean, addSynonymsOnly: boolean, disableNewMenuItems: boolean, orgId: string }) {
        setLoadingPOSMenuSync(true)
        const request: UpdateMenuRequest = {
            ...options,
            locationId: application.posLocationId,
            posName: application.posName as POSNames,
            manageMenuApplicationId: application.manageMenuApplicationId,
            liveApplicationId: application.id,
            updateLiveApplication: false,
            skipEntityExapnsion: true,
            skipMenuAugmentation: true,
        }
        const result = await voicifyApi.initiateMenuUpdate(request)
        if (result?.resultType !== "Ok") {
            setErrors(result.errors)
        }
        setLoadingPOSMenuSync(false)
        setShowConfirmationModal(false)
    }

    async function publishAllChanges(application: ApplicationModel, orgId: string) {
        setLoadingPublishAllChanges(true);
        // generate a guid
        const processId = uuidv4();
        const request: SyncMenusRequest = {
            menuConfiguration: {
                primaryLocation: {
                    manageMenuApplicationId: application.manageMenuApplicationId,
                    manageMenuApplicationName: application.name,
                    liveApplicationId: application.id,
                    liveApplicationName: application.name,
                }
            },
            orgId: orgId,
            processId: processId
        }
        const result = await voicifyApi.syncMenus(request)

        if (result?.resultType !== "Ok") {
            setErrors(result.errors)
        }
        menuSyncContainer.startMenuSync(application.id, processId);
        setLoadingPublishAllChanges(false)
        setShowConfirmationModal(false)
    }

    const findOptionGroupAndSizeGroupsNeedAugment = (menuItemToUse: MenuItemModel, diffKeys: { key: string, diff: string }[]) => {
        const optionGroupsThatNeedReAugment = []
        const sizeGroupsThatNeedReAugment = []
        for (const diff of diffKeys) {
            const key = diff.key; //optionGroups.1.isDisabled
            const path = key?.split(".");

            let isOptionGroup = false;
            let isSizeGroup = false;
            let isOption = false;
            let isSize = false;

            //get the last optionGroup or sizeGroup
            for (const property of path.slice().reverse()) {//slice so that it does not mutate the original
                if (property === "optionGroups") {
                    isOptionGroup = true;
                    break;
                }
                else if (property === "sizeGroups") {
                    isSizeGroup = true;
                    break;
                } else if (property === "sizes") {
                    isSize = true;
                    break;
                } else if (property === "options") {
                    isOption = true;
                    break;
                }
            }

            let numberOfStepsBack = -3;
            if (key.includes("numberOfRequiredOptions")) {
                numberOfStepsBack = -1;
            }
            const mainGroupPath = path.slice(0, numberOfStepsBack) //-3 to go back to the main group eg. "optionGroups".1.isDisabled
            let changedGroup = getItemByPath(menuItemToUse, mainGroupPath, diff.diff) ?? menuItemToUse["sizeGroups"][0]?.sizes.find(s => normalizeName(s.name) === normalizeName(sizeName))?.optionGroups[0]
            if (changedGroup && key.includes("numberOfRequiredOptions")) {
                if (isOptionGroup) {
                    optionGroupsThatNeedReAugment.push({ changedGroup, index: mainGroupPath.slice(-1) });
                }
                if (isSizeGroup && typeof mainGroupPath.slice(-1)[0] === "string") {
                    sizeGroupsThatNeedReAugment.push({ changedGroup, index: parseInt(mainGroupPath.slice(-1)[0]) });
                }
            }
            if (changedGroup && (key.includes("colloquialName") || key.includes("isDisabled"))) {
                if (isOption) {
                    optionGroupsThatNeedReAugment.push({ changedGroup, index: mainGroupPath.slice(-1) });
                }
                if ((isSizeGroup || isSize) && typeof mainGroupPath.slice(-1)[0] === "string") {
                    sizeGroupsThatNeedReAugment.push({ changedGroup, index: parseInt(mainGroupPath.slice(-1)[0]) });
                }
            }
        }
        return {
            optionGroupsThatNeedReAugment,
            sizeGroupsThatNeedReAugment
        }
    }

    /**
     * get the clean/required data needed for augmenting the menu item
     * @returns null if the menu item does not require augment
     */
    function getCleanedMenuItemForAugment() {
        let menuItemToAugment: {
            itemId: string,
            optionGroupReferenceIdsToAugment: string[],
            sizeGroupIndeciesToAugment: number[]
        } | null = null;
        const diffs = findChangesInMenuItems(originalMenuItem, menuItem);

        const { optionGroupsThatNeedReAugment, sizeGroupsThatNeedReAugment } = findOptionGroupAndSizeGroupsNeedAugment(menuItem, diffs)

        const optionGroupReferenceIds = optionGroupsThatNeedReAugment.map(obj => obj.changedGroup.referenceId).filter(item => item);
        const sizeGroupIndecies = sizeGroupsThatNeedReAugment.map(obj => obj.index);

        //is a reaugment needed
        if (optionGroupReferenceIds.length > 0 || sizeGroupIndecies.length > 0) {
            menuItemToAugment = {
                itemId: menuItem.id,
                optionGroupReferenceIdsToAugment: [...new Set(optionGroupReferenceIds)],
                sizeGroupIndeciesToAugment: sizeGroupIndecies?.length ? [0] : []
            };

        }
        return menuItemToAugment
    }

    function getItemByPath(obj: any, path: string[], matchName: string): any {
        let item = obj;
        for (let i = 0; i < path.length; i++) {
            const pathI = path[i];
            if (i !== 0 && path[i - 1] === "optionGroups") {
                if (matchName) {
                    item = item?.find(i => normalizeName(i.name) === normalizeName(matchName))
                } else {
                    item = item?.find(i => i.referenceId === pathI);
                }

            } else if (i !== 0 && path[i - 1] === "sizeGroups") {
                if (item?.length) {
                    item = item[0];
                } else {
                    item = null;
                }
            } else if (i !== 0 && path[i - 1] === "options") {
                item = item?.find(i => i.id === pathI);
            } else if (i !== 0 && path[i - 1] === "sizes") {
                item = item?.find(i => i.id === pathI);
            } else {
                if (item && (pathI in item || Number(pathI) in item)) {
                    item = item[isNaN(Number(pathI)) ? pathI : Number(pathI)]
                }

            }

        }
        return item;
    }

    function findChangesInMenuItems(original: MenuItemModel = originalMenuItem, current: MenuItemModel = menuItem, skipIfOriginalDisabled = false): any {
        let differences: Record<string, any> = {};
        function findDifferences(obj1: any, obj2: any, path: string[] = []) {
            const isDisabledCase = (skipIfOriginalDisabled && obj1?.isDisabled);
            if (isDisabledCase) return;
            // If the objects are the same, no need to record a difference.
            if (_.isEqual(obj1, obj2)) return;
            if (_.isArray(obj1) && _.isArray(obj2)) {
                let lastKey = "";
                let optionGroups = false;
                let sizeGroups = false;
                let optionsSizes = false;
                if (path?.length) {
                    lastKey = path[path.length - 1];
                    if (lastKey === "optionGroups") {
                        optionGroups = true;
                    } else if (lastKey === "sizeGroups") {
                        sizeGroups = true;
                    } else if (lastKey === "options" || lastKey === "sizes") {
                        optionsSizes = true;
                    }
                }
                if (optionGroups) {
                    const allIds = new Set([...obj1.map(o => o.referenceId), ...obj2.map(o => o.referenceId)]);
                    allIds.forEach(key => {
                        if (!key?.includes("editId")) {
                            const o1 = obj1.find(o1 => o1.referenceId === key);
                            const o2 = obj2.find(o2 => o2.referenceId === key);
                            findDifferences(o1, o2, [...path, key]);
                        }
                    });
                } if (sizeGroups) {
                    const sizeGroup1 = obj1?.length ? obj1[0] : null;
                    const sizeGroup2 = obj2?.length ? obj2[0] : null;
                    findDifferences(sizeGroup1, sizeGroup2, [...path, "sizeGroup"]);
                } else if (optionsSizes) {
                    const allIds = new Set([...obj1.map(o => o.id), ...obj2.map(o => o.id)]);
                    allIds.forEach(key => {
                        if (!key?.includes("editId")) {
                            const o1 = obj1.find(o1 => o1.id === key);
                            const o2 = obj2.find(o2 => o2.id === key);
                            findDifferences(o1, o2, [...path, key]);
                        }
                    });
                } else {
                    // we need to order arrays by id if we have it, then name, then reference id, then original name
                    let sorted1 = obj1?.filter(item => !skipIfOriginalDisabled || !item?.isDisabled);
                    sorted1 = sorted1.sort((a, b) => {
                        if (a?.id?.length) {
                            return a.id > b.id ? 1 : -1;
                        } else if (a?.originalName?.length) {
                            return a.originalName > b.originalName ? 1 : -1;
                        } else if (a?.referenceId?.length) {
                            return a.referenceId > b.referenceId ? 1 : -1;
                        } else if (a?.name?.length) {
                            return a.name > b.name ? 1 : -1;
                        }
                        return 1;
                    });
                    const sorted2 = obj2?.sort((a, b) => {
                        if (a?.id?.length) {
                            return a.id > b.id ? 1 : -1;
                        } else if (a?.originalName?.length) {
                            return a.originalName > b.originalName ? 1 : -1;
                        } else if (a?.referenceId?.length) {
                            return a.referenceId > b.referenceId ? 1 : -1;
                        } else if (a?.name?.length) {
                            return a.name > b.name ? 1 : -1;
                        }
                        return 1;
                    });
                    const allKeys = new Set([...Object.keys(sorted1), ...Object.keys(sorted2)]);
                    allKeys.forEach(key => {
                        if (!key.includes("editId"))
                            findDifferences(sorted1[key], sorted2[key], [...path, key]);
                    });
                }
            } else if (_.isObject(obj1) && _.isObject(obj2)) {
                const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
                allKeys.forEach(key => {
                    if (!key.includes("editId"))
                        findDifferences(obj1[key], obj2[key], [...path, key]);
                });
            }
            else {
                differences[path.join('.')] = { original: obj1, current: obj2 };
            }
        }

        findDifferences(original, current);
        return Object.entries(differences).map(([k, v]) => ({
            key: k,
            value: v
        }))
    }

    interface RequiredItemsInDiff {
        key: string,
        value: {
            current: string | boolean | undefined, original: string | boolean
        }
    }
    const handleItemNameDescriptionCategoryChange = (modified: string, foundMatchedItems: MatchedItems[], diff: RequiredItemsInDiff) => {
        let existingItem = foundMatchedItems.find(t => t.updatedItem.id === menuItem.id)
        if (existingItem) {
            existingItem.diffs.push({
                key: diff.key,
                diff: modified,
                newValue: diff.value?.current ?? false,
                originalValue: diff.value?.original ?? false,
            })
        } else {
            const data = {
                updatedItem: menuItem, //It is already updated
                diffs: [{
                    key: diff.key,
                    diff: modified,
                    newValue: diff.value?.current ?? false,
                    originalValue: diff.value?.original ?? false,
                }]
            }
            foundMatchedItems.push(data)
        }

    }

    const normalizeName = (name: string) => name.trim().toLowerCase();
    const deepEqual = (obj1, obj2) => {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }

    /**
     * Finds the matching items based on the current changes.
     * @param applicationId 
     */
    async function askToUpdateOtherItems(applicationId: string): Promise<MatchedItems[]> {

        const diffs = findChangesInMenuItems(originalMenuItem, menuItem)
        const prompts = ["initialPromptOverride",
            "simpleInitialPromptOverride",
            "numberedInitialPromptOverride",
            "promptOverride", "colloquialName", "isDisabled"]

        setFetchingMenu(true);
        let entireMenu: MenuItemModel[];
        const result = await retrieveEntireMenu(applicationId)
        if (result.resultType === "Ok") {
            entireMenu = JSON.parse(JSON.stringify(result.data))
        }
        setFetchingMenu(false);
        const formattedMenuItems: EditedMenuItemModel[] = entireMenu.map((menuItem) => {
            return { menuItem: menuItem }
        });
        const foundMatchedItems: MatchedItems[] = [];

        const loadedGroups = interactionModelContainer.loadGroups(formattedMenuItems)
        for (const diff of diffs) {
            if (diff.key.includes("persistentProperties") || diff.key.includes("editId")) {
                continue
            }
            //If an option inside the sizeGroups is changed:
            let sizeName = "";
            let sizeGroupIdInKey = "";
            if (diff.key.includes("sizeGroups") && diff.key.includes("optionGroups") && diff.key.split(".")[0] === "sizeGroups") {
                sizeGroupIdInKey = diff.key.split(".").slice(0, 4)[3]; //Get the size Id from the key
                sizeName = menuItem.sizeGroups[0].sizes.find(size => size.id === sizeGroupIdInKey).name;
                setSizeName(sizeName) //The sizeName is used in the augmentation process to find the optionGroup
                diff.key = diff.key.split(".").slice(4).join('.'); //Remove sizeGroups.sizes and change the it to optionGroups.<refID>.options.<refId>.isDisabled.
            }
            const optionGroupOrSizeGroupKey = diff.key.split(".")// optionGroups.<refID>.options.<refID>.isDisabled ||    optionGroups.<refID>.options.<refID>.optionGroups.<refID>.options.<refID>
            const mainPropertyKey = optionGroupOrSizeGroupKey[0] //        ↑ look here
            const editedIndex = optionGroupOrSizeGroupKey[3]
            const propertyToChange = optionGroupOrSizeGroupKey[optionGroupOrSizeGroupKey.length - 1]

            let editedOptionGroupName: string

            let editedSizeGroupPrompt: string
            let editedSizeGroupName: string

            let modified: string
            let originalOptionName = ""
            let modifiedPath: string[] = [];
            let currentGroup: MenuItemModel | MenuItemOptionGroup | MenuItemOption = menuItem
            let currentOptionGroup: any
            if (mainPropertyKey === "colloquialName" || mainPropertyKey === "attributes") {

                switch (mainPropertyKey) {
                    case "colloquialName":
                        handleItemNameDescriptionCategoryChange("Item Referred to As", foundMatchedItems, diff);
                        break;

                    case "attributes": //attributes.data.description
                        let text: string
                        if (optionGroupOrSizeGroupKey[1] === "data" && optionGroupOrSizeGroupKey[2] === "category") {
                            text = "Category"
                        }
                        else if (optionGroupOrSizeGroupKey[1] === "description") {
                            text = "Description"
                        } else {
                            break;
                        }
                        handleItemNameDescriptionCategoryChange(text, foundMatchedItems, diff);
                        break;
                }

                continue;
            }

            //Finding the changes made:
            for (let i = 0; i < optionGroupOrSizeGroupKey.length; i++) {
                const key = optionGroupOrSizeGroupKey[i];
                if (key === "optionGroups" && currentGroup && 'optionGroups' in currentGroup) {
                    const groupIndex = optionGroupOrSizeGroupKey[++i]; // Get the index of the optionGroup
                    //If there is a sizeGroupId key, it means that an optionGroup is edited in a sizeGroup
                    if (sizeGroupIdInKey !== "" && "sizeGroups" in currentGroup) {
                        currentGroup = currentGroup.sizeGroups[0].sizes?.find(og => og?.id === sizeGroupIdInKey).optionGroups?.find(og => og?.referenceId === groupIndex) as MenuItemOptionGroup;
                    } else {
                        currentGroup = currentGroup.optionGroups?.find(og => og?.referenceId === groupIndex) as MenuItemOptionGroup;
                    }
                    currentOptionGroup = currentGroup;
                    editedOptionGroupName = currentGroup?.name ?? currentGroup?.referenceId;
                    modifiedPath.push(currentGroup?.name ?? currentGroup?.referenceId); // Append the name of the optionGroup
                } else if (key === "options" && currentGroup && 'options' in currentGroup) {
                    const optionIndex = optionGroupOrSizeGroupKey[++i]; // Get the index of the option
                    currentGroup = currentGroup.options?.find(o => o.id === optionIndex) as MenuItemOption;
                    if (currentGroup) {
                        modifiedPath.push(currentGroup.name); // Append the name of the option
                        originalOptionName = currentGroup.name;
                    }
                } else if (key === "promptIfNoSelections") { //eg: key optionGroups.0.options.1.promptIfNoSelections
                    modifiedPath.push(propertyToChange);
                } else if (key === "numberOfRequiredOptions") {
                    modifiedPath.push(propertyToChange);
                } else if (key === "isDisabled") {
                    modifiedPath.push(propertyToChange);
                }
            }

            let valueToUpdate
            if (mainPropertyKey === "optionGroups" || diff.key.includes("options")) {
                modified = modifiedPath.join(" - ")
                if (currentGroup) {
                    valueToUpdate = currentGroup[propertyToChange];
                }
            } else if (mainPropertyKey === "sizeGroups") {
                editedSizeGroupPrompt = menuItem.sizeGroups[0]?.promptOverride
                modified = `${editedSizeGroupPrompt}`
                valueToUpdate = menuItem.sizeGroups[0][propertyToChange]
                if (editedIndex) {
                    editedSizeGroupName = menuItem.sizeGroups[0]?.sizes?.find(s => s.id === editedIndex)?.name
                    modified = `${editedSizeGroupPrompt} -  ${editedSizeGroupName}`
                    valueToUpdate = menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex)[propertyToChange]
                }
            }
            if (mainPropertyKey === "sizeGroups" && !diff.key.includes("optionGroups")) {
                entireMenu.forEach(item => {
                    for (const sizeGroup of item.sizeGroups) {
                        if (sizeGroup.promptOverride === editedSizeGroupPrompt) {
                            let existingItem = foundMatchedItems.find(t => t.updatedItem.id === item.id)
                            let originalValue: string | boolean
                            if (editedIndex) {
                                const targetSize = sizeGroup.sizes.find(s =>
                                    s.name.toLowerCase() === menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex).name.toLowerCase());
                                if (targetSize) {
                                    originalValue = targetSize[propertyToChange]
                                    targetSize[propertyToChange] = valueToUpdate
                                    targetSize.persistentProperties = menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex)?.persistentProperties
                                } else { // We need to skip if it doesn't have the size
                                    return;
                                }
                            } else {
                                if (propertyToChange === "isDisabled") {
                                    originalValue = sizeGroup[propertyToChange]
                                    sizeGroup[propertyToChange] = valueToUpdate
                                }
                            }

                            if (existingItem) {
                                const targetSizeExisting = existingItem.updatedItem.sizeGroups.find(s => s.promptOverride === editedSizeGroupPrompt)
                                if (editedIndex) {
                                    const targetSizeInExisting = targetSizeExisting?.sizes?.find(s =>
                                        s.name.toLowerCase() === menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex).name.toLowerCase());
                                    if (!targetSizeInExisting) {
                                        return;
                                    }
                                    originalValue = targetSizeInExisting[propertyToChange]
                                    targetSizeInExisting[propertyToChange] = valueToUpdate
                                    targetSizeInExisting.persistentProperties = menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex)?.persistentProperties
                                } else {
                                    originalValue = targetSizeExisting[propertyToChange]
                                    targetSizeExisting[propertyToChange] = valueToUpdate
                                    targetSizeExisting.persistentProperties = menuItem.sizeGroups[0].sizes?.find(s => s.id === editedIndex)?.persistentProperties
                                }
                                if (originalValue === valueToUpdate) {
                                    break;
                                }
                                existingItem.diffs.push({
                                    key: diff.key,
                                    diff: modified,
                                    newValue: diff.value?.current ?? false,
                                    originalValue: originalValue ?? false,
                                })
                            } else {
                                if (originalValue === valueToUpdate) {
                                    break;
                                }
                                const data = {
                                    updatedItem: item,
                                    diffs: [{
                                        key: diff.key,
                                        diff: modified,
                                        newValue: diff.value?.current ?? false,
                                        originalValue: originalValue ?? false,
                                    }]
                                }
                                foundMatchedItems.push(data)
                            }
                        }

                    }
                })
            } else {
                //Find the optionGroups by edited optionGroup name
                const matches = loadedGroups.uniqueGroups.filter(uniqueGroup => {
                    if ("optionGroup" in uniqueGroup) {
                        if (uniqueGroup.optionGroup.name.toLowerCase() === editedOptionGroupName?.toLowerCase()) {
                            //If prompt is changed in the advanced settings. The number of options in the optionGroup has to match. The names should also match
                            if (!diff.key.includes("options") && prompts.includes(propertyToChange)) {
                                if (currentOptionGroup.options.length === uniqueGroup.optionGroup.options.length) {
                                    const sortedArr1 = currentOptionGroup.options.sort((a: MenuItemOption, b: MenuItemOption) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
                                    const sortedArr2 = uniqueGroup.optionGroup.options.sort((a: MenuItemOption, b: MenuItemOption) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
                                    for (let i = 0; i < sortedArr1.length; i++) {
                                        if (normalizeName(sortedArr1[i].name) !== normalizeName(sortedArr2[i].name)) {
                                            return false;
                                        }
                                    }
                                    return true
                                } else {
                                    return false
                                }
                            }

                            // For option-level changes, only match if the option exists in the group
                            if (originalOptionName) {
                                return uniqueGroup.optionGroup.options.some(option => normalizeName(option.name) === normalizeName(originalOptionName));
                            }
                            // For group-level changes (like isDisabled), only match if options are exactly the same
                            else if (propertyToChange === "isDisabled" || propertyToChange === "promptIfNoSelections" || propertyToChange === "numberOfRequiredOptions") {
                                if (currentOptionGroup.options.length !== uniqueGroup.optionGroup.options.length) {
                                    return false;
                                }
                                const sortedCurrent = currentOptionGroup.options.sort((a, b) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
                                const sortedUnique = uniqueGroup.optionGroup.options.sort((a, b) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
                                return sortedCurrent.every((option, index) =>
                                    normalizeName(option.name) === normalizeName(sortedUnique[index].name)
                                );
                            }
                        }
                    }
                    return false
                })
                //Find the items with matches optionGroup
                let matchingItemsIndexes: string[][] = []
                for (const match of matches) {
                    const foundItem = loadedGroups.matchingGroups.find(obj => match.editId in obj)?.[match.editId]
                    if (foundItem) {
                        matchingItemsIndexes.push(foundItem)
                    }
                }
                let itemOriginalValue = { value: "", id: "", persistentProperties: currentGroup?.persistentProperties }
                for (const itemIndexes of matchingItemsIndexes) {
                    let existingItems = foundMatchedItems.filter(it => itemIndexes.includes(it.updatedItem.id))
                    if (existingItems.length > 0) {//If item already exists, only update the specific property and push new data to diffs
                        for (const existingItem of existingItems) {
                            let whatsChanged = buildWhatsChanged(sizeGroupIdInKey, sizeName, editedOptionGroupName, originalOptionName);
                            let optionGroupToUse = getOptionGroupToUse(existingItem.updatedItem, sizeGroupIdInKey, sizeName)

                            if (optionGroupToUse === null) {
                                continue
                            }
                            itemOriginalValue.id = existingItem.updatedItem.id
                            findOptionGroupByNameAndUpdateValue(optionGroupToUse, editedOptionGroupName, propertyToChange, originalOptionName, valueToUpdate, prompts, itemOriginalValue, diff.key)//Find and update the existingItem
                            if (itemOriginalValue.value === valueToUpdate) {
                                break;
                            }
                            const newData = {
                                key: diff.key,
                                diff: editedOptionGroupName,
                                newValue: diff.value?.current ?? false,
                                whatsChanged,
                                originalValue: itemOriginalValue.value ?? false,
                            }

                            //Make sure it doesn't exist
                            if (!existingItem.diffs.some(obj => deepEqual(obj, newData))) {
                                existingItem.diffs.push(newData)
                            }
                        }
                    } else {
                        let foundItemsInMenu = entireMenu.filter(menuI => itemIndexes.includes(menuI.id))
                        for (const foundItemInMenu of foundItemsInMenu) {
                            let whatsChanged = buildWhatsChanged(sizeGroupIdInKey, sizeName, editedOptionGroupName, originalOptionName);
                            let optionGroupToUse = getOptionGroupToUse(foundItemInMenu, sizeGroupIdInKey, sizeName)
                            if (optionGroupToUse === null) {
                                continue
                            }
                            itemOriginalValue.id = foundItemInMenu.id
                            findOptionGroupByNameAndUpdateValue(optionGroupToUse, editedOptionGroupName, propertyToChange, originalOptionName, valueToUpdate, prompts, itemOriginalValue, diff.key)

                            if (itemOriginalValue.value === valueToUpdate) {
                                break;
                            }

                            const data = {
                                updatedItem: foundItemInMenu,
                                diffs: [{
                                    key: diff.key,
                                    diff: editedOptionGroupName,
                                    whatsChanged,
                                    newValue: diff.value?.current ?? false,
                                    originalValue: itemOriginalValue.value ?? false,
                                }]
                            }
                            foundMatchedItems.push(data)
                        }
                    }

                }
            }
            const itemIndex = foundMatchedItems.findIndex(item => item.updatedItem.originalName.toLowerCase() === menuItem.originalName.toLowerCase());
            if (itemIndex !== -1) {
                const [item] = foundMatchedItems.splice(itemIndex, 1);
                foundMatchedItems.unshift(item);
            }

        }
        setmatchingItemsWithSameOptionGroupOrSizeGroup(foundMatchedItems)
        if (foundMatchedItems.length > 1) {
            setShowAskToUpdateOtherItemsModal(true)
        }
        return foundMatchedItems;
    }

    const getOptionGroupToUse = (item: MenuItemModel, sizeGroupIdInKey: string, sizeName: string) => {
        let optionGroupToUse = item.optionGroups;

        if (sizeGroupIdInKey !== "") {
            const foundSize = item.sizeGroups[0].sizes.find(size => normalizeName(size.name) === normalizeName(sizeName));
            if (!foundSize) {
                return null; // or return whatever makes sense here
            }
            optionGroupToUse = foundSize.optionGroups;
        }

        return optionGroupToUse;
    }
    const buildWhatsChanged = (sizeGroupIdInKey: string, sizeName: string, editedOptionGroupName: string, originalOptionName: string) => {
        if (sizeGroupIdInKey !== "") {
            return `Sizes - ${sizeName} - ${editedOptionGroupName} - ${originalOptionName}`;
        }
        return `${editedOptionGroupName} - ${originalOptionName}`;
    }

    async function saveAndAugmentAll(application: ApplicationModel, orgId: string) {
        const applicationId = application.manageMenuApplicationId;
        const menuItemsToAugment = []
        let entireMenu = menuItems
        if (entireMenu.length === 0) {
            const result = await retrieveEntireMenu(applicationId)
            if (result.resultType === "Ok") {
                entireMenu = result.data
            }
        }

        const organizedOptionGroupToAugment: { menuItemId: string, optionGroupRefIds: string[] }[] = Object.values(
            optionGroupsToAugment.reduce((acc, { optionGroupRefId, menuItemId }) => {
                // Initialize the object for this menuItemId if it doesn't exist
                if (!acc[menuItemId]) {
                    acc[menuItemId] = { menuItemId, optionGroupRefIds: [] };
                }
                // Add the optionGroupRefId to the array for this menuItemId
                acc[menuItemId].optionGroupRefIds.push(optionGroupRefId);
                return acc;
            }, {})
        );
        const isGenAIFoodOrdering = application.featureFlags?.some(flag => flag.id === GenAIFoodOrdering)
        for (const matchedItem of matchingItemsWithSameOptionGroupOrSizeGroup) {
            //If item id is in the itemsToNotAugment, do not save or augment it
            if (itemsToNotAugment.includes(matchedItem.updatedItem.id)) {
                continue
            }
            const { optionGroupsThatNeedReAugment, sizeGroupsThatNeedReAugment } = findOptionGroupAndSizeGroupsNeedAugment(matchedItem.updatedItem, matchedItem.diffs)

            let optionGroupReferenceIds = optionGroupsThatNeedReAugment.map(obj => obj.changedGroup.referenceId).filter(item => item);

            const matchedItemOptionGroups = organizedOptionGroupToAugment.find(it => it.menuItemId === matchedItem.updatedItem.id)
            if (matchedItemOptionGroups) {
                optionGroupReferenceIds = matchedItemOptionGroups.optionGroupRefIds
            }

            const sizeGroupIndecies = sizeGroupsThatNeedReAugment.map(obj => obj.index);
            //Augment needed
            if (optionGroupReferenceIds.length > 0 || sizeGroupIndecies.length > 0) {
                menuItemsToAugment.push({
                    itemId: matchedItem.updatedItem.id,
                    optionGroupReferenceIdsToAugment: [...new Set(optionGroupReferenceIds)],
                    sizeGroupIndeciesToAugment: sizeGroupIndecies?.length ? [0] : []
                });
                if (menuItemsToAugment.length > 0 && !isGenAIFoodOrdering) {
                    matchedItem.updatedItem.lastAugmentStartTime = new Date().getTime().toString();//Set the start time
                }
            }
            // Save the updated menu items
            const menuUpdateResponse = await applicationNlpMenuContainer.updateVoicifyMenuItem(applicationId, matchedItem.updatedItem);
            if (menuUpdateResponse.resultType !== "Ok") {
                setErrors(menuUpdateResponse.errors)
            }

        }
        const augmentRequest = {
            applicationId,
            forceAugment: true,
            generateMenuItemColloquialNames: false,//no need to regenerate colloquial name for the menu item
            generateForOptionGroups: true,
            generateForSizeGroups: true,
            propertiesToAugment: [],
            menuItemsToAugment: menuItemsToAugment,
            orgId: orgId
        }
        if (menuItemsToAugment.length > 0 && !isGenAIFoodOrdering) {
            applicationNlpMenuContainer.augmentMenu(augmentRequest);
            setMenuItemAugmentStatus("loading");
        }

        setShowAskToUpdateOtherItemsModal(false)
    }


    /**
     * Finds the matching option in a menu item and updates the value
     * @param optionGroups 
     * @param groupName option group name
     * @param propertyToChange 
     * @param optionOriginalName the unchanged name of the option
     * @param newValue value to be set
     * @returns null
     */
    const findOptionGroupByNameAndUpdateValue = (optionGroups: MenuItemOptionGroup[], groupName: string, propertyToChange: keyof MenuItemOptionGroup, optionOriginalName: string, newValue: string | boolean | number, prompts: string[], itemOriginalValue: { value: string | boolean, id: string, persistentProperties?: string[] }, key: string) => {
        // Base case: Iterate over all option groups at the current level
        for (let group of optionGroups) {
            // Check if the current optionGroup name matches the groupName
            if (normalizeName(group.name) === normalizeName(groupName)) {
                // Handle prompt changes at the optionGroup level
                if (!key.includes("options") && prompts.includes(propertyToChange)) {
                    itemOriginalValue.value = group[propertyToChange] as string | boolean
                    (group as any)[propertyToChange] = newValue
                    group.persistentProperties = itemOriginalValue.persistentProperties
                    if (propertyToChange === "colloquialName") {
                        setOptionGroupsToAugment(prev => [...prev, { optionGroupRefId: group.referenceId, menuItemId: itemOriginalValue.id }])
                    }
                    return
                }

                const countOptionGroups = (key.match(/optionGroups/g) || []).length;
                const countOptions = (key.match(/options/g) || []).length;

                // Only update the group properties if we're at the correct nesting level
                if ((countOptionGroups - countOptions === 1) &&
                    (propertyToChange === "isDisabled" || propertyToChange === "numberOfRequiredOptions" || propertyToChange === "promptIfNoSelections") &&
                    propertyToChange in group) {
                    // Store the original value before updating
                    itemOriginalValue.value = group[propertyToChange] as string | boolean

                    // Only update if the value is actually different
                    if (group[propertyToChange] !== newValue) {
                        (group as any)[propertyToChange] = newValue
                        group.persistentProperties = itemOriginalValue.persistentProperties

                        if (propertyToChange === "isDisabled" || propertyToChange === "numberOfRequiredOptions") {
                            setOptionGroupsToAugment(prev => [...prev, { optionGroupRefId: group.referenceId, menuItemId: itemOriginalValue.id }])
                        }
                    }
                    return
                }

                // Handle option-level changes
                if (optionOriginalName) {
                    const foundOption = group.options.find(option => normalizeName(option.name) === normalizeName(optionOriginalName))
                    if (foundOption) {
                        itemOriginalValue.value = foundOption[propertyToChange] as string | boolean
                        // Only update if the value is actually different
                        if (foundOption[propertyToChange] !== newValue) {
                            foundOption[propertyToChange] = newValue
                            foundOption.persistentProperties = itemOriginalValue.persistentProperties
                            if (propertyToChange === "colloquialName") {
                                setOptionGroupsToAugment(prev => [...prev, { optionGroupRefId: group.referenceId, menuItemId: itemOriginalValue.id }])
                            }
                        }
                        return
                    }
                }
            }

            // Recursively check nested option groups
            if (group.options) {
                for (let option of group.options) {
                    if (option.optionGroups?.length > 0) {
                        const result = findOptionGroupByNameAndUpdateValue(
                            option.optionGroups,
                            groupName,
                            propertyToChange,
                            optionOriginalName,
                            newValue,
                            prompts,
                            itemOriginalValue,
                            key
                        );
                        if (result !== null) {
                            return result;
                        }
                    }
                }
            }
        }

        return null;
    }

    const getMenuItemsDiff = async (application: ApplicationModel) => {
        setLoadingMenuDiff(true);
        try {
            const newMenu = await applicationNlpMenuContainer.getTransformedMenu(
                application.manageMenuApplicationId,
                application.posName,
                application.posLocationId
            );

            if (newMenu.errors?.length > 0 || !newMenu.data || !newMenu.data.data) {
                setErrors(newMenu.errors ?? ["An error occurred while fetching the menu."]);
                setLoadingMenuDiff(false);
                return;
            }

            const differences: Record<string, string[]> = {};
            const transformedMenuItems = newMenu.data?.data?.menuItems || [];
            const currentMenuItems = menuItems || [];

            // Check for removed items (in current but not in transformed)
            for (const currentItem of currentMenuItems) {
                const exists = transformedMenuItems.some(t =>
                    normalizeName(t.originalName) === normalizeName(currentItem.originalName)
                );
                if (!exists) {
                    differences[currentItem.originalName] = ["Item removed"];
                }
            }

            // Check for new items and changes (in transformed but not in current)
            for (const transformedItem of transformedMenuItems) {
                let currentItem = currentMenuItems.find(c =>
                    normalizeName(c.originalName) === normalizeName(transformedItem.originalName)
                );

                const currentItemById = currentMenuItems.find(c =>
                    normalizeName(c.id) === normalizeName(transformedItem.id)
                );

                if (currentItemById) {
                    currentItem = currentItemById;
                }

                if (!currentItem) {
                    differences[transformedItem.originalName] = ["New item"];
                } else {
                    const itemDifferences = getMenuItemDifferences(currentItem, transformedItem);
                    if (itemDifferences.length > 0) {
                        differences[currentItem.originalName] = itemDifferences;
                    }
                }
            }

            setLoadingMenuDiff(false);
            return differences;
        } catch (error) {
            setErrors([error.toString()]);
            setLoadingMenuDiff(false);
            return {};
        }
    };

    const getMenuItemDifferences = (current: MenuItemModel, transformed: MenuItemModel): string[] => {
        const differences: string[] = [];

        if (current.originalName !== transformed.originalName) {
            differences.push("Name changed");
        }

        // Check option groups
        if ((current.optionGroups?.length || 0) !== (transformed.optionGroups?.length || 0)) {
            differences.push("Number of option groups changed");
        } else if (current.optionGroups && transformed.optionGroups) {
            const optionChanges = getOptionGroupChanges(current.optionGroups, transformed.optionGroups);
            differences.push(...optionChanges);
        }

        // Check size groups
        if ((current.sizeGroups?.length || 0) !== (transformed.sizeGroups?.length || 0)) {
            differences.push("Number of size groups changed");
        } else if (current.sizeGroups && transformed.sizeGroups) {
            const sizeChanges = getSizeGroupChanges(current.sizeGroups, transformed.sizeGroups);
            differences.push(...sizeChanges);
        }

        return differences;
    };

    const getOptionGroupChanges = (current: MenuItemOptionGroup[], transformed: MenuItemOptionGroup[]): string[] => {
        const changes: string[] = [];

        for (const currentGroup of current) {
            const transformedGroup = transformed.find(g => g.name === currentGroup.name);
            if (!transformedGroup) {
                changes.push(`option group '${currentGroup.name}' removed`);
                continue;
            }

            // Compare options within groups
            const currentOptions = currentGroup.options?.map(o => o.name) || [];
            const transformedOptions = transformedGroup.options?.map(o => o.name) || [];

            const addedOptions = transformedOptions.filter(o => !currentOptions.includes(o));
            const removedOptions = currentOptions.filter(o => !transformedOptions.includes(o));

            addedOptions.forEach(added => {
                changes.push(`option - ${added} added`);
            });

            removedOptions.forEach(removed => {
                changes.push(`option - ${removed} removed`);
            });

            for (let currentOption of currentGroup.options) {
                let transformedOption = transformedGroup.options.find(o => o.id == currentOption.id);
                if (!transformedOption) {
                    transformedOption = transformedGroup.options.find(o => o.name == currentOption.name);
                }

                if (transformedOption) {
                    let currentOptionCopy = JSON.parse(JSON.stringify(currentOption));

                    currentOptionCopy.persistentProperties = undefined;
                    currentOptionCopy.attributes.persistentProperties = undefined;
                    currentOptionCopy.disableInferencing = undefined;
                    currentOptionCopy.isDisabled = undefined;
                    transformedOption.modifier = undefined;

                    if (!areJsonsEqual(currentOptionCopy, transformedOption)) {
                        changes.push(`option - ${currentOption.name} changed`);
                    }
                }
            }
        }

        // Check for new groups
        const newGroups = transformed.filter(g => !current.some(c => c.name === g.name));
        newGroups.forEach(newGroup => {
            changes.push(`option group '${newGroup.name}' added`);
        });

        return changes;
    };

    const getSizeGroupChanges = (current: MenuItemSizeGroup[], transformed: MenuItemSizeGroup[]): string[] => {
        const changes: string[] = [];

        for (let i = 0; i < current.length; i++) {
            const currentSizes = current[i].sizes?.map(s => s.name) || [];
            const transformedSizes = transformed[i].sizes?.map(s => s.name) || [];

            const addedSizes = transformedSizes.filter(s => !currentSizes.includes(s));
            const removedSizes = currentSizes.filter(s => !transformedSizes.includes(s));

            addedSizes.forEach(added => {
                changes.push(`size - ${added} added`);
            });

            removedSizes.forEach(removed => {
                changes.push(`size - ${removed} removed`);
            });
        }

        return changes;

    }


    function areJsonsEqual(a: object, b: object) {
        return deepEqualIgnoringExtra(a, b) && deepEqualIgnoringExtra(b, a);
    }

    function deepEqualIgnoringExtra(a: any, b: any): boolean {
        if (a === b) return true;

        if (typeof a !== typeof b) return false;

        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((item, index) => deepEqualIgnoringExtra(item, b[index]));
        }

        if (typeof a === 'object' && a !== null && b !== null) {
            const sharedKeys = Object.keys(a).filter(key => key in b);
            return sharedKeys.every(key => deepEqualIgnoringExtra(a[key], b[key]));
        }

        return false;
    }


    return {
        menuItem,
        activeEditId,
        path,
        isLoadingMenu,
        loadingMenuDiff,
        menuItems,
        categories,
        isMenuItemEdited,
        menuItemAugmentStatus,
        setActiveEditId,
        setMenuItem,
        setPath,
        getMenu,
        getLiveMenu,
        setCategories,
        setMenuItems,
        resetMenuItemSelections,
        updateMenuItem,
        updateMenu,
        handleDisableEnableCategory,
        syncMenuWithPOS,
        showConfirmationModal,
        setShowConfirmationModal,
        errors,
        loadingPOSMenuSync,
        publishAllChanges,
        ladingPublishAllChanges,
        setOriginalMenuItem,
        getMenuItem,
        setMenuItemAugmentStatus,
        getCleanedMenuItemForAugment,
        showAskToUpdateOtherItemsModal,
        setShowAskToUpdateOtherItemsModal,
        askToUpdateOtherItems,
        setmatchingItemsWithSameOptionGroupOrSizeGroup,
        matchingItemsWithSameOptionGroupOrSizeGroup,
        findChangesInMenuItems,
        itemsToNotAugment,
        setItemsToNotAugment,
        saveAndAugmentAll,
        pendingMenuItemUpdates,
        fetchingMenu,
        getMenuItemsDiff,
        loadMenuSyncModule,
        menuSyncRequest,
        submitMenuSyncRequest
    };
}

const MenuContainer = createContainer(useMenuContainer);
export default MenuContainer;
